import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Activity, Database, Clock, TrendingUp, RefreshCw } from 'lucide-react';

const StatisticsPage = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/admin/statistics', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      
      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatistics, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };

  if (loading && !statistics) {
    return (
      <Layout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <Alert variant="destructive">
            <AlertDescription>
              {error}
              <Button onClick={fetchStatistics} className="ml-4" size="sm">
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Statistics</h1>
          <p className="text-muted-foreground">System and API usage metrics</p>
        </div>
        <Button onClick={fetchStatistics} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* API Usage Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total API Calls</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(statistics?.api_usage?.total_calls || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Since deployment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful Calls</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatNumber(statistics?.api_usage?.successful_calls || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {statistics?.api_usage?.total_calls > 0 
                ? `${((statistics.api_usage.successful_calls / statistics.api_usage.total_calls) * 100).toFixed(1)}% success rate`
                : 'No calls yet'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed Calls</CardTitle>
            <Activity className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatNumber(statistics?.api_usage?.failed_calls || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {statistics?.api_usage?.total_calls > 0 
                ? `${((statistics.api_usage.failed_calls / statistics.api_usage.total_calls) * 100).toFixed(1)}% failure rate`
                : 'No failures'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics?.api_usage?.average_response_time 
                ? formatDuration(statistics.api_usage.average_response_time)
                : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Per API request
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Database Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Database Statistics</CardTitle>
          <CardDescription>MongoDB collection counts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center space-x-4">
              <Database className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Users</p>
                <p className="text-2xl font-bold">
                  {formatNumber(statistics?.database?.users_count || 0)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Database className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Registrations</p>
                <p className="text-2xl font-bold">
                  {formatNumber(statistics?.database?.registrations_count || 0)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Database className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">API Logs</p>
                <p className="text-2xl font-bold">
                  {formatNumber(statistics?.database?.api_logs_count || 0)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
          <CardDescription>Current system status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium">System Uptime</span>
              <span className="text-sm text-muted-foreground">
                {statistics?.system?.uptime 
                  ? formatDuration(statistics.system.uptime)
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">Redis Status</span>
              <span className={`text-sm font-semibold ${
                statistics?.system?.redis_connected ? 'text-green-600' : 'text-red-600'
              }`}>
                {statistics?.system?.redis_connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">MongoDB Status</span>
              <span className={`text-sm font-semibold ${
                statistics?.system?.mongodb_connected ? 'text-green-600' : 'text-red-600'
              }`}>
                {statistics?.system?.mongodb_connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium">EventsAir Token</span>
              <span className={`text-sm font-semibold ${
                statistics?.system?.eventsair_token_valid ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {statistics?.system?.eventsair_token_valid ? 'Valid' : 'Needs Refresh'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent API Activity */}
      {statistics?.api_usage?.recent_endpoints && (
        <Card>
          <CardHeader>
            <CardTitle>Most Used Endpoints</CardTitle>
            <CardDescription>Top API endpoints by call count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(statistics.api_usage.recent_endpoints)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([endpoint, count]) => (
                  <div key={endpoint} className="flex items-center justify-between">
                    <span className="text-sm font-mono">{endpoint}</span>
                    <span className="text-sm font-semibold">{formatNumber(count)} calls</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </Layout>
  );
};

export default StatisticsPage;
